// Accumulates base64 PCM16 (mono, 16kHz) and can finalize as a WAV Blob.
import type { WavEncoder } from "./encoders/wav-encoder";
import { ManualWavEncoder } from "./encoders/manual-wav-encoder";
import { desktopPcmMime } from "./desktop-pcm-persistence";

export class DesktopAudioAssembler {
  private pending: Int16Array[] = [];
  private encoder: WavEncoder;

  constructor(encoder: WavEncoder = new ManualWavEncoder()) {
    this.encoder = encoder;
  }

  appendBase64Pcm16(bufferBase64: string): Int16Array {
    const bytes = Uint8Array.from(atob(bufferBase64), c => c.charCodeAt(0));
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const sampleCount = Math.floor(bytes.byteLength / 2);
    const samples = new Int16Array(sampleCount);
    for (let i = 0; i < sampleCount; i++) samples[i] = view.getInt16(i * 2, true);
    this.pending.push(samples);
    return samples;
  }

  async finalizeWav(sampleRate = 16000): Promise<Blob> {
    const all = concatInt16(this.pending);
    return this.encoder.encodePCM16Mono(all, sampleRate);
  }

  flushPendingToPcmBlob(): Blob | null {
    if (this.pending.length === 0) return null;
    const all = concatInt16(this.pending);
    this.pending = [];
    return new Blob([all.buffer], { type: desktopPcmMime() });
  }

  getPendingPcmBlob(): Blob | null {
    if (this.pending.length === 0) return null;
    const all = concatInt16(this.pending);
    return new Blob([all.buffer], { type: desktopPcmMime() });
  }

  static async createWavFromPcmBlobs(
    blobs: Blob[],
    sampleRate = 16000,
    encoder: WavEncoder = new ManualWavEncoder(),
    tailBlob?: Blob | null
  ): Promise<Blob> {
    const pcmBlobs = tailBlob ? [...blobs, tailBlob] : blobs.slice();
    const arrays: Int16Array[] = [];
    for (const b of pcmBlobs) {
      const buf = await b.arrayBuffer();
      arrays.push(new Int16Array(buf));
    }
    const all = concatInt16(arrays);
    return encoder.encodePCM16Mono(all, sampleRate);
  }

  reset() {
    this.pending = [];
  }
}

function concatInt16(chunks: Int16Array[]): Int16Array {
  const total = chunks.reduce((acc, a) => acc + a.length, 0);
  const out = new Int16Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    out.set(chunk, offset);
    offset += chunk.length;
  }
  return out;
}
